(() => {
  const SELECTOR = '[data-elite-home-hero-carousel]';
  const AUTOPLAY_DELAY = 6500;

  const initCarousel = (hero) => {
    if (hero.dataset.eliteHomeHeroCarouselReady === 'true') return;

    const slides = Array.from(hero.querySelectorAll('[data-elite-home-hero-slide]')).sort((firstSlide, secondSlide) => {
      const firstOrder = Number.parseInt(firstSlide.dataset.carouselOrder || '999', 10);
      const secondOrder = Number.parseInt(secondSlide.dataset.carouselOrder || '999', 10);
      return firstOrder - secondOrder;
    });
    const controlsRoot = hero.querySelector('[data-elite-home-hero-controls]');
    const previousControl = hero.querySelector('[data-elite-home-hero-previous]');
    const nextControl = hero.querySelector('[data-elite-home-hero-next]');
    const renderedControls = Array.from(controlsRoot?.querySelectorAll('[data-elite-home-hero-control]') || []);
    const controls = renderedControls.length === slides.length
      ? renderedControls
      : slides.length > 1 && controlsRoot
        ? slides.map((slide, index) => {
            const control = document.createElement('button');
            control.className = `elite-home-hero__carousel-dot${index === 0 ? ' is-active' : ''}`;
            control.type = 'button';
            control.setAttribute('aria-label', slide.dataset.carouselLabel || `Show banner ${index + 1}`);
            control.setAttribute('aria-current', index === 0 ? 'true' : 'false');
            control.dataset.eliteHomeHeroControl = '';
            return control;
          })
        : [];

    if (!slides.length || (slides.length > 1 && controls.length !== slides.length)) return;

    if (controls.length && controls[0].parentElement !== controlsRoot) {
      controlsRoot?.replaceChildren(...controls);
    }

    const mobileViewport = window.matchMedia('(max-width: 760px)');
    const usesMobileHero = () => hero.hasAttribute('data-mobile-static-hero') && mobileViewport.matches;
    const updateMobileHeroTop = () => {
      if (!usesMobileHero()) {
        hero.style.removeProperty('--elite-mobile-hero-top');
        return;
      }
      // Reserve the real header/announcement height above the hero. CSS uses svh
      // so Safari opening its toolbars cannot push the carousel arrows off screen.
      const top = Math.max(0, Math.ceil(hero.getBoundingClientRect().top + window.scrollY));
      hero.style.setProperty('--elite-mobile-hero-top', `${top}px`);
    };
    updateMobileHeroTop();
    window.addEventListener('resize', updateMobileHeroTop, { passive: true });
    window.addEventListener('pageshow', updateMobileHeroTop);
    document.fonts?.ready.then(updateMobileHeroTop);
    const header = document.querySelector('.elite-header');
    if (header && 'ResizeObserver' in window) {
      new ResizeObserver(updateMobileHeroTop).observe(header);
    }
    // The static mobile hero uses its own catalogue photograph.
    if (!usesMobileHero()) {
      hero.querySelectorAll('.is-active img').forEach(image => {
        image.decode?.().catch(() => {});
      });
    }

    hero.dataset.eliteHomeHeroCarouselReady = 'true';
    let activeIndex = 0;
    let timer = null;
    let touchStartX = 0;
    let touchStartY = 0;
    let touchStarted = false;
    let lastSwipeAt = 0;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const stop = () => {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    };

    const showSlide = (index) => {
      activeIndex = (index + slides.length) % slides.length;

      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === activeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        slide.toggleAttribute('inert', !isActive);
      });

      controls.forEach((control, controlIndex) => {
        const isActive = controlIndex === activeIndex;
        control.classList.toggle('is-active', isActive);
        control.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    };

    const start = () => {
      stop();
      if (
        usesMobileHero() ||
        slides.length < 2 ||
        reducedMotion.matches ||
        hero.matches(':hover') ||
        hero.contains(document.activeElement)
      ) {
        return;
      }
      timer = window.setInterval(() => showSlide(activeIndex + 1), AUTOPLAY_DELAY);
    };

    controls.forEach((control, index) => {
      control.addEventListener('click', () => {
        showSlide(index);
        start();
      });
    });

    previousControl?.addEventListener('click', () => {
      showSlide(activeIndex - 1);
      start();
    });

    nextControl?.addEventListener('click', () => {
      showSlide(activeIndex + 1);
      start();
    });

    const beginSwipe = (clientX, clientY) => {
      touchStarted = true;
      touchStartX = clientX;
      touchStartY = clientY;
      stop();
    };

    const endSwipe = (clientX, clientY) => {
      if (!touchStarted) return;
      touchStarted = false;

      const deltaX = clientX - touchStartX;
      const deltaY = clientY - touchStartY;
      const isHorizontalSwipe = Math.abs(deltaX) >= 45 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2;

      if (isHorizontalSwipe && Date.now() - lastSwipeAt > 350) {
        lastSwipeAt = Date.now();
        showSlide(activeIndex + (deltaX < 0 ? 1 : -1));
      }

      start();
    };

    hero.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 1) return;
        beginSwipe(event.touches[0].clientX, event.touches[0].clientY);
      },
      { passive: true }
    );

    hero.addEventListener(
      'touchend',
      (event) => {
        if (!event.changedTouches.length) return;
        endSwipe(event.changedTouches[0].clientX, event.changedTouches[0].clientY);
      },
      { passive: true }
    );

    hero.addEventListener(
      'touchcancel',
      () => {
        touchStarted = false;
        start();
      },
      { passive: true }
    );

    hero.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      beginSwipe(event.clientX, event.clientY);
    });

    hero.addEventListener('pointerup', (event) => {
      if (!event.isPrimary) return;
      endSwipe(event.clientX, event.clientY);
    });

    hero.addEventListener('pointercancel', () => {
      touchStarted = false;
      start();
    });

    hero.addEventListener('mouseenter', stop);
    hero.addEventListener('mouseleave', start);
    hero.addEventListener('focusin', stop);
    hero.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!hero.contains(document.activeElement)) start();
      }, 0);
    });

    hero.addEventListener('shopify:block:select', (event) => {
      const selectedSlide = event.target.closest('[data-elite-home-hero-slide]');
      const selectedIndex = slides.indexOf(selectedSlide);
      if (selectedIndex < 0) return;
      showSlide(selectedIndex);
      stop();
    });

    hero.addEventListener('shopify:block:deselect', start);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    });

    reducedMotion.addEventListener?.('change', start);
    mobileViewport.addEventListener?.('change', start);
    showSlide(0);
    start();
  };

  const initAll = () => {
    document.querySelectorAll(SELECTOR).forEach(initCarousel);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
